// PriceShowList の表示スタイル（D4: 巨大コンポーネントからスタイル定義を分離）。
// 動的なCSS変数（--zoom-level 等）はJS状態に連動するため、関数で組み立てる。
// 生成される <style> の内容は分離前と同一（挙動・見た目は不変）。

export const buildPriceShowListStyles = (
  zoomLevel: number,
  columnWidths: { fixedCol1: number; fixedCol2: number; fixedCol3: number; medicineName: number },
  fixedOffsets: { col2: number; col3: number; col4: number },
  getFixedColumnWidth: (baseWidth: number, minZoomLevel?: number) => number
): string => `
          :root {
            --zoom-level: ${zoomLevel};
            --base-font-size: 16px;
            --fixed-col1-width: ${getFixedColumnWidth(columnWidths.fixedCol1)}px;
            --fixed-col2-width: ${getFixedColumnWidth(columnWidths.fixedCol2)}px;
            --fixed-col3-width: ${getFixedColumnWidth(columnWidths.fixedCol3)}px;
            --fixed-col4-width: ${Math.ceil(columnWidths.medicineName * 16 * zoomLevel)}px;
            --fixed-col2-left: ${fixedOffsets.col2}px;
            --fixed-col3-left: ${fixedOffsets.col3}px;
            --fixed-col4-left: ${fixedOffsets.col4}px;
          }
          table.my-table > thead > tr > th,
          table.my-table > tbody > tr > td,
          table.my-table > tfoot > tr > td {
            padding: 0.2rem 0.5rem;
            line-height: 1.2;
            white-space: nowrap;
            position: relative;
            vertical-align: middle;
          }

          .my-table tbody:not(.disabled-row) tr:nth-child(5) th,
          .my-table tbody:not(.disabled-row) tr:nth-child(5) td {
            padding-bottom: 0.8rem; /* 下側の padding を大きくする */
          }
          
          /* グレーアウト行では余分なパディングを削除 */
          .disabled-row tr:nth-child(5) td {
            padding-bottom: 0.2rem !important;
          }
          
          /* Sticky header styles */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 20;
            background-color: white;
          }
          
          .sticky-header th {
            background-color: white !important;
          }
          
          /* Fix for sticky positioning with borders */
          .sticky-header::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: -3px;
            height: 3px;
            background-color: var(--bs-success);
            z-index: 21;
          }
          
          /* 上部スクロールバーのスタイル */
          .top-scrollbar-container {
            position: sticky;
            top: 0;
            z-index: 25;
            background-color: white;
            border-bottom: 1px solid #dee2e6;
            height: 20px;
            overflow-x: auto;
            overflow-y: hidden;
          }
          
          /* Fixed columns styles */
          .fixed-col-1 {
            position: sticky !important;
            left: 0px !important;
            z-index: 15 !important;
            background-color: white !important;
            width: var(--fixed-col1-width) !important;
            min-width: var(--fixed-col1-width) !important;
            max-width: var(--fixed-col1-width) !important;
          }
          
          .fixed-col-2 {
            position: sticky !important;
            left: var(--fixed-col2-left) !important;
            z-index: 14 !important;
            background-color: white !important;
            width: var(--fixed-col2-width) !important;
            min-width: var(--fixed-col2-width) !important;
            max-width: var(--fixed-col2-width) !important;
          }
          
          .fixed-col-3 {
            position: sticky !important;
            left: var(--fixed-col3-left) !important;
            z-index: 13 !important;
            background-color: white !important;
            width: var(--fixed-col3-width) !important;
            min-width: var(--fixed-col3-width) !important;
            max-width: var(--fixed-col3-width) !important;
            overflow: visible !important;
          }
          
          .fixed-col-4 {
            position: sticky !important;
            left: var(--fixed-col4-left) !important;
            z-index: 13 !important;
            background-color: white !important;
            width: var(--fixed-col4-width) !important;
            min-width: var(--fixed-col4-width) !important;
            max-width: var(--fixed-col4-width) !important;
          }
          
          /* Shadow effect for fixed columns */
          .fixed-col-4::after {
            content: '';
            position: absolute;
            top: 0;
            right: -4px;
            bottom: 0;
            width: 4px;
            background: linear-gradient(to right, rgba(0,0,0,0.1), transparent);
            pointer-events: none;
          }
          
          /* Border for last fixed column */
          table.my-table > thead > tr > th.fixed-column::after,
          table.my-table > tbody > tr > td.fixed-column::after {
            content: '';
            position: absolute;
            top: -1px;
            right: 0;
            height: calc(100% + 2px);
            width: 2px;
            background-color: var(--bs-success);
            z-index: 10;
          }
          
          /* グレーアウト行でも緑線を連続させる */
          .disabled-row td.fixed-column::after {
            top: -1px;
            height: calc(100% + 2px);
            background-color: var(--bs-success) !important;
          }
          
          /* Prevent column overlap */
          table.my-table {
            border-spacing: 0;
            border-collapse: collapse;
          }
          
          /* 行間の隙間を防ぐ */
          table.my-table tbody tr {
            border: none;
          }
          
          table.my-table tbody tr td {
            border-top: none;
            border-bottom: none;
          }
          
          /* tbodyタグ間の隙間を防ぐ */
          table.my-table tbody {
            border: none;
            margin: 0;
            padding: 0;
          }
          
          /* 連続するtbody要素間の隙間をなくす */
          table.my-table tbody + tbody {
            border-top: none;
          }
          
          /* グレーアウト行の境界を完全に削除 */
          .disabled-row tr {
            border: none !important;
          }
          
          .disabled-row tr td {
            border: none !important;
            padding-top: 0.2rem;
            padding-bottom: 0.2rem;
          }
          
          /* 後発・AG・BSバッジのセルでoverflow visibleを確保 */
          td.medicine-icon {
            overflow: visible !important;
          }
          
          /* 後発・AG・BSバッジの枠線を確実に表示 */
          td.medicine-icon > div {
            box-sizing: border-box;
            border: 1px solid var(--bs-success) !important;
            flex-shrink: 0; /* バッジのサイズを固定 */
          }
          
          /* 100%以下のズームレベルでバッジが切れないように調整 */
          @media (max-width: 1200px) {
            td.fixed-col-3.medicine-icon {
              padding-right: 2px !important;
            }
          }
          
          /* 低ズームレベルでの最小幅を設定 */
          .fixed-col-1 {
            min-width: ${Math.ceil(columnWidths.fixedCol1 * 16 * 0.7)}px;
          }
          .fixed-col-2 {
            min-width: ${Math.ceil(columnWidths.fixedCol2 * 16 * 0.7)}px;
          }
          .fixed-col-3 {
            min-width: ${Math.ceil(columnWidths.fixedCol3 * 16 * 0.7)}px;
          }
          .fixed-col-4 {
            min-width: ${Math.ceil(20 * 16 * 0.7)}px;
          }
          
          /* Force fixed columns to maintain their position */
          .fixed-col-1, .fixed-col-2, .fixed-col-3, .fixed-col-4 {
            transform: translateZ(0);
            will-change: transform;
            box-sizing: border-box;
          }
          
          
          
          /* デバッグ情報 */
          /* zoomLevel: ${zoomLevel}, fixedOffsets: ${JSON.stringify(fixedOffsets)} */
          
          /* Ensure proper stacking in header */
          .sticky-header .fixed-col-1 { z-index: 35 !important; }
          .sticky-header .fixed-col-2 { z-index: 34 !important; }
          .sticky-header .fixed-col-3 { z-index: 33 !important; }
          .sticky-header .fixed-col-4 { z-index: 32 !important; }
          
          /* アイコン列が品名列に隠れないようにz-indexを調整 */
          td.fixed-col-3 {
            z-index: 14 !important;
          }
          
          td.fixed-col-4 {
            z-index: 13 !important;
          }
          
          /* アイコンのバッジ自体のz-indexを上げる */
          td.medicine-icon > div {
            position: relative;
            z-index: 15;
          }
          
          /* 列幅調整スタイル */
          .column-resizer {
            position: absolute;
            top: 0;
            right: 0;
            width: 5px;
            height: 100%;
            background-color: transparent;
            cursor: col-resize;
            z-index: 2;
          }
          
          .column-resizer:hover {
            background-color: rgba(0, 123, 255, 0.3);
          }
          /* グレーアウト行は背景色のみ変更し、文字色は通常のまま */
          .disabled-row {
            /* opacity は削除して背景色のみで制御 */
          }
          
          /* アイコン部分の背景色も調整 */
          .disabled-row .medicine-icon {
            /* opacityは不要になったため削除 */
          }
          
          /* 固定列の背景色を確実に白にする */
          .fixed-col-1, .fixed-col-2, .fixed-col-3, .fixed-col-4 {
            background-color: white !important;
          }
          
          /* 無効化された行の固定列の背景色をグレーに変更 */
          .disabled-row .fixed-col-1, 
          .disabled-row .fixed-col-2, 
          .disabled-row .fixed-col-3, 
          .disabled-row .fixed-col-4 {
            background-color: #d6d6d6 !important;
          }
          
          /* グレーアウト行で行間の白い線を防ぐ */
          .disabled-row td.fixed-col-1,
          .disabled-row td.fixed-col-2,
          .disabled-row td.fixed-col-3,
          .disabled-row td.fixed-col-4 {
            position: relative;
          }
          
          /* すべての固定列で行間の隙間を背景色で埋める */
          .disabled-row td.fixed-col-1::before,
          .disabled-row td.fixed-col-2::before,
          .disabled-row td.fixed-col-3::before,
          .disabled-row td.fixed-col-4:not(.fixed-column)::before {
            content: '';
            position: absolute;
            top: -1px;
            bottom: -1px;
            left: 0;
            right: 0;
            background-color: inherit;
            z-index: -1;
          }
          
          /* fixed-columnクラスがある場合は右端を少し内側にする */
          .disabled-row td.fixed-col-4.fixed-column::before {
            content: '';
            position: absolute;
            top: -1px;
            bottom: -1px;
            left: 0;
            right: 3px; /* 緑線の幅分を空ける */
            background-color: inherit;
            z-index: -1;
          }
          
          /* テーブルコンテナのスクロール設定 */
          .table-container {
            overflow-x: auto;
            overflow-y: auto;
            position: relative;
          }
`;
